import {
  CalendarDays,
  ClipboardCheck,
  FolderKanban,
  Home,
  Network,
  type LucideIcon,
} from 'lucide-react';

import { can, type Ability, type Role } from '@/shared/constants/roles';
import { initials } from '@/shared/lib/format';
import type { ScopeBinding } from '@/shared/types/domain';

/**
 * Os pontos de partida do painel. As regras abaixo são o ADR-012
 * (`docs/adr/ADR-012-navegacao-por-caso-de-uso.md`) em forma curta; ele tem o
 * porquê de cada uma e as opções descartadas.
 *
 * **1. A sidebar responde a PERGUNTAS.** A hierarquia da igreja (filial →
 * coordenação → ministério → projeto) responde a "onde isso mora", e isso é
 * conteúdo, não navegação. Coordenação e ministério são rota de detalhe
 * (`/coordenacoes/:id`, `/ministerios/:id`), alcançada pela aba Coordenações ou
 * pelo nome do ministério em qualquer lista.
 *
 * **2. Todo item é um LUGAR ou uma FILA.** Lugar responde "onde vejo X" e nunca
 * tem número — ⚠️ *mas nem todo lugar vale para todos*: a ADR-015 tirou
 * "Projetos" do menu de `leader`/`assistant` (decisão 97), pelo mecanismo da
 * regra 3 abaixo. O que continua valendo é que lugar não tem contador. Fila responde "o que falta eu fazer", é
 * trabalho de alguém e SEMPRE tem contador — item de fila sem número promete
 * trabalho sem dizer quanto, a pessoa clica, não acha nada e aprende a não
 * clicar. Se você não consegue dizer qual dos dois é, não é item: é filtro.
 *
 * **3. Papel REMOVE item. Nunca renomeia, reordena nem substitui.** ⚠️ Isto
 * corrige o que a versão anterior deste docblock dizia ("papel não muda o
 * mapa") — a frase estava certa em agosto e errada em uma rodada: a v1 tem
 * duas plateias assimétricas (`admin`/`overseer` decidem toda semana;
 * `leader`/`assistant` submetem duas ou três vezes por ano), e fingir que a
 * sidebar é idêntica para as duas custava clareza dos dois lados. A regra que
 * fica: a sidebar do líder é subconjunto ESTRITO da do overseer — mesmos
 * rótulos, mesma ordem, menos linhas. É o que mantém o suporte por telefone
 * de pé — "se você não vê Análise aí, é porque esse acesso não é seu" é uma
 * explicação; "o seu menu está diferente do meu" não é. Visibilidade é
 * `ability`, resolvido por `can()` — nunca um segundo mecanismo.
 *
 * **4. Fila entra numa SEÇÃO própria, agrupada por DE QUEM é o trabalho** —
 * nunca por substantivo/tabela que ela lê. "Análise" não mora sob um
 * cabeçalho "Projetos" só porque as duas leem `projects`: colocá-la ali
 * ensinaria que a fila é um tipo de lista de projetos, e a pessoa passaria a
 * procurá-la dentro de `/projetos`.
 *
 * **5. Recorte que a RLS já faz não vira item.** "Meus Projetos" não existe:
 * `projects_select` recorta por `my_ministry_ids()`, que para líder e auxiliar
 * devolve só o próprio ministério — o item seria idêntico a Projetos para
 * eles, e vazio por construção para o coordenador. "Meus" é filtro
 * (`?escopo=meus`) e card no Início.
 *
 * **6. Seção "Minha área" é condicional ao DADO, não ao papel.** Um item por
 * vínculo VIGENTE (`scopeNavItems`): o ministério para quem lidera, a
 * coordenação para quem coordena. Ela cresce com os chapéus daquela pessoa,
 * nunca com o que a secretaria cadastra em outro lugar da igreja.
 *
 * ⚠️ **O limiar caiu de "2+ vínculos" para 1** — a ADR-012 decisão 4 pedia dois
 * porque a seção era desambiguação ("de qual chapéu você fala?"), e com um
 * vínculo só não havia o que desambiguar. Ela deixou de ser isso: com o
 * Coordenações restrita a `admin`/`overseer` (decisão 5, agora alcançando
 * `coordinator` também), esta seção é o ÚNICO caminho de quem tem vínculo
 * operacional até o que é dele. Um limiar de dois deixaria a maioria — quem
 * lidera um ministério — sem caminho nenhum.
 *
 * ⚠️ **É também a única exceção à regra 3**, e ela é de forma, não de rótulo: a
 * sidebar do líder deixa de ser subconjunto estrito da do overseer porque ganha
 * itens que NÃO são do vocabulário do sistema, e sim do vínculo da pessoa. O
 * suporte por telefone continua de pé pelo mesmo argumento de lá — "Missões
 * aparece aí porque você lidera Missões" é uma explicação. O que a regra 3
 * segue governando inteiro são os itens ESTÁTICOS de `NAV_ITEMS`.
 */
export interface NavItem {
  to: string;
  label: string;
  /**
   * Glifo do Lucide, ou um **monograma** (`'ML'`) quando o item vem de dado.
   *
   * A união existe por causa do modo padrão da barra, que é `hover` e em
   * repouso tem 68px — ou seja, **só ícones**. Dois ministérios com o mesmo
   * glifo genérico seriam dois itens visualmente idênticos justamente na
   * configuração que a maioria nunca troca; e não há glifo do Lucide que
   * signifique "Mídia e Transmissão". O monograma é a única forma que
   * distingue sem inventar iconografia por ministério.
   */
  icon: LucideIcon | string;
  /** Uma frase para o `title`/leitor de tela, em linguagem de voluntário. */
  description: string;
  /** `/` casa com tudo; só ele precisa de comparação exata. */
  end?: boolean;
  /**
   * Prefixos de rota que este item POSSUI além do próprio `to`.
   *
   * Existe porque permalink de entidade não mora sob a aba que o mostra:
   * `/ministerios/:id` e `/coordenacoes/:id` são conteúdo da aba Coordenações e não
   * carregam esse prefixo, de propósito (ver o docblock de `router.tsx` — o
   * endereço de um ministério não deve depender de por onde se chegou nele).
   * Sem esta lista, abrir um ministério apagaria a marca da sidebar inteira.
   */
  owns?: readonly string[];
  /**
   * Rotas que este item ADOTA — mas só quando o dono canônico delas não está
   * visível para aquela pessoa (decisão 97).
   *
   * ⚠️ **Existe por causa de um buraco que a decisão 97 abriu.** Com `/projetos`
   * fora do menu de `leader`/`assistant`, o permalink `/projetos/:id` ficava sem
   * aba nenhuma para eles: a marca sumia da sidebar no meio da navegação e a
   * tela do projeto perdia o "voltar" — exatamente o defeito que `owns` foi
   * criado para resolver, reaparecendo por outro caminho.
   *
   * **Não é `owns` com outro nome**, e a diferença importa: `owns` é posse
   * incondicional, e duas abas jamais acendem para a mesma rota
   * (`isNavItemActive` continua devolvendo uma só). Adoção é condicional à
   * VISIBILIDADE, e por isso vive em `activeNavItem`, que já conhece a lista
   * recortada por papel.
   *
   * Para o líder, adotar `/projetos/:id` no Calendário é a leitura certa e não
   * um remendo: é de lá que ele chega ao projeto, é para lá que ele volta, e é
   * o que a [ADR-015](../../docs/adr/ADR-015-agenda-volatil.md) §6 diz que a
   * tela passou a ser.
   */
  fallbackFor?: readonly string[];
  /**
   * Visibilidade (ADR-012 decisão 2/3). Ausente = todo mundo vê — é um
   * LUGAR. Presente = só quem tem a capacidade — é FILA ou console de quem
   * decide. `can()` já existe em `roles.ts`; não inventar um segundo
   * mecanismo de visibilidade aqui.
   */
  ability?: Ability;
  /**
   * Agrupa itens de fila sob um cabeçalho próprio, abaixo de um separador
   * (regra 4). `undefined` = item de topo, sem seção.
   */
  section?: string;
}

export const NAV_ITEMS: readonly NavItem[] = [
  {
    to: '/',
    label: 'Início',
    icon: Home,
    description: 'O que está acontecendo no seu escopo',
    end: true,
  },
  {
    to: '/calendario',
    label: 'Calendário',
    icon: CalendarDays,
    description: 'O que a igreja tem marcado',
    // Ver `fallbackFor`: para quem não tem "Projetos" no menu (líder e
    // auxiliar), o permalink do projeto — e a gaveta `/rascunhos` — passa a
    // pertencer a esta aba. Para quem tem, nada muda: o dono canônico vence.
    fallbackFor: ['/projetos', '/rascunhos'],
    // Sem `ability` (é um LUGAR: os cinco papéis veem), sem `section`, **sem
    // contador** — item de lugar nunca tem número (ADR-012 regra 2).
    //
    // Sem `owns`: o calendário não possui permalink nenhum. O permalink do
    // projeto continua sendo de "Projetos"; quem chega a ele pelo calendário
    // mantém esta aba acesa por `location.state.from`, que é enfeite de
    // navegação e morre no F5 (decisão 16).
  },
  {
    to: '/projetos',
    label: 'Projetos',
    icon: FolderKanban,
    description: 'Todos os projetos que você acompanha',
    /**
     * ⚠️ **`/rascunhos` volta a ser a GAVETA daqui**, como era antes da decisão
     * 97. Aquela decisão a promoveu a item de topo ("Minhas pendências") porque,
     * com "Projetos" fora do menu do líder, a tela ficaria inalcançável para
     * quem tem pendência — mas o item foi retirado do menu para todos os papéis,
     * e a rota não pode ficar órfã de aba: sem `owns`, abrir `/rascunhos`
     * apagaria a marca da sidebar e a trilha inteira.
     *
     * Quem entra por aqui continua entrando pelo botão "Ver rascunhos" no
     * cabeçalho de `/projetos`; quem salva um rascunho continua caindo na tela
     * (`ProjectFormPage`). Ver `fallbackFor` do Calendário para o líder, que não
     * tem "Projetos" no menu.
     */
    owns: ['/rascunhos'],
    /**
     * ⚠️ **Some para `leader` e `assistant`** (decisão 97, ADR-015 §6). Para
     * eles o ciclo inteiro cabe no **Calendário** (ver, criar e retomar pela
     * caixa de entrada); a listagem que atravessa meses
     * e a busca por título são pergunta de quem acompanha MAIS de um ministério.
     *
     * `readOwnCoordination` é a capacidade certa porque é literalmente essa a
     * fronteira: quem enxerga além do próprio ministério. Não é uma lista de
     * papéis escrita à mão aqui — visibilidade é `ability`, nunca um segundo
     * mecanismo (regra 3).
     *
     * **A ROTA continua existindo para todos**: link direto e favorito
     * continuam abrindo, e a RLS não mudou. O que sai é o item do menu.
     */
    ability: 'readOwnCoordination',
  },
  {
    /**
     * ⚠️ **Era `/organograma`, e o nome saiu junto com o endereço.** A tela
     * nunca desenhou hierarquia nenhuma: ela lista as coordenações da filial,
     * e é por elas que se chega ao ministério. "Organograma" prometia um
     * diagrama e cobrava do leitor a tradução até o que a tela é.
     *
     * O endereço antigo continua abrindo, por `Navigate` (ver `router.tsx`), e
     * por isso NÃO precisa estar em `owns`: rota que só redireciona nunca fica
     * montada para ter aba acesa.
     *
     * `/coordenacoes/:id` também fica fora de `owns` — o prefixo do próprio
     * `to` já o alcança. Quem continua precisando de adoção explícita é
     * `/ministerios/:id`, que mora em outra família de endereço.
     */
    to: '/coordenacoes',
    label: 'Coordenações',
    icon: Network,
    description: 'A estrutura da igreja e quem responde por ela',
    owns: ['/ministerios'],
    /**
     * ⚠️ **Some para `coordinator`, `leader` e `assistant`** — a ADR-012 decisão
     * 5 já mandava tirá-lo do líder e do auxiliar, e nunca foi implementada; o
     * coordenador entrou junto porque o argumento é o mesmo para ele.
     *
     * A razão não é hierarquia, é **honestidade da tela**. `ministries_select`
     * recorta por `my_coordination_ids()` e `projects_select` por
     * `my_ministry_ids()`: quem não enxerga a filial abre esta tela e recebe
     * os ministérios IRMÃOS com todos os contadores zerados. O desenho de painel
     * promete atividade que a RLS não vai entregar, e o zero parece "não tem
     * nada marcado" quando a verdade é "não é seu". Para essas três pessoas o
     * caminho até o que é delas passou a ser a seção "Minha área", que mostra o
     * mesmo dado sem a moldura que mente.
     *
     * `readOwnBranch` é a capacidade certa porque é literalmente a fronteira em
     * jogo: quem enxerga a filial inteira é para quem a estrutura da filial
     * inteira significa alguma coisa.
     *
     * **A ROTA continua existindo para todos** (mesmo precedente da decisão 97):
     * link direto e favorito continuam abrindo, e a RLS não mudou. O que sai é
     * o item do menu.
     */
    ability: 'readOwnBranch',
  },
  {
    to: '/analise',
    label: 'Análise',
    icon: ClipboardCheck,
    description: 'Projetos aguardando sua decisão',
    ability: 'reviewProject',
    section: 'Minha atribuição',
  },
];

/** Rótulo da seção de vínculos. Uma constante porque a sidebar e o teste leem o mesmo. */
export const SCOPE_SECTION = 'Minha área';

/** Itens visíveis para os papéis vigentes da pessoa — a regra 3 em código. */
export function visibleNavItems(roles: readonly Role[]): readonly NavItem[] {
  return NAV_ITEMS.filter((item) => !item.ability || can(roles, item.ability));
}

/**
 * A seção "Minha área": um item por vínculo vigente (regra 6).
 *
 * **Sem `ability`, e é o ponto todo** — quem tem um vínculo tem o item porque
 * TEM o vínculo, não porque um papel foi listado aqui. Um overseer sem
 * ministério recebe lista vazia sem que nada neste arquivo saiba o que é um
 * overseer, e no dia em que a secretaria der um ministério a ele o item aparece
 * sozinho.
 *
 * **A ordem é determinística e isso é requisito, não capricho**: a barra é
 * memória muscular, e um item que troca de posição entre duas cargas custa mais
 * que a navegação que ele oferece. Coordenações antes de ministérios (o
 * continente antes do conteúdo, para quem tem os dois chapéus), cada grupo por
 * nome, com `localeCompare` porque "Ação Social" tem de vir antes de "Adoração"
 * — em ordem de code point, o `Ç` cai depois de todo o alfabeto.
 *
 * O item de coordenação ADOTA `/ministerios` (`fallbackFor`): o coordenador
 * chega ao ministério filho pela tela da coordenação, e sem a adoção a marca da
 * sidebar sumiria no clique seguinte — a aba Coordenações, que possui esse
 * prefixo, não está visível para ele. Ministério não adota nada: é folha.
 */
export function scopeNavItems(bindings: readonly ScopeBinding[]): readonly NavItem[] {
  const byName = [...bindings].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

  return [
    ...byName
      .filter((binding) => binding.kind === 'coordination')
      .map((binding) => ({
        to: `/coordenacoes/${binding.id}`,
        label: binding.name,
        icon: initials(binding.name),
        description: `Os ministérios de ${binding.name}`,
        fallbackFor: ['/ministerios'],
        section: SCOPE_SECTION,
      })),
    ...byName
      .filter((binding) => binding.kind === 'ministry')
      .map((binding) => ({
        to: `/ministerios/${binding.id}`,
        label: binding.name,
        icon: initials(binding.name),
        description: `Os projetos de ${binding.name}`,
        section: SCOPE_SECTION,
      })),
  ];
}

/**
 * Toda rota pertence a uma aba, e é ela que fica acesa — inclusive nas rotas de
 * detalhe.
 *
 * Antes isto era o casamento por prefixo que o `NavLink` faz sozinho, e o
 * resultado era assimétrico sem que nada no código dissesse por quê:
 * `/projetos/:id` acendia "Projetos" porque a URL começa igual, enquanto
 * `/ministerios/:id` não acendia nada — a marca simplesmente sumia da sidebar
 * no meio da navegação. Com `owns` a relação rota → aba passa a ser declarada
 * num lugar só, e as duas famílias de permalink se comportam igual.
 *
 * Função pura de propósito: é o tipo de regra que se testa lendo, e o teste
 * pega o dia em que alguém acrescentar uma rota sem dizer de quem ela é.
 */
export function isNavItemActive(item: NavItem, pathname: string): boolean {
  if (item.end) return pathname === item.to;
  return [item.to, ...(item.owns ?? [])].some(
    // O `/` no fim é o que separa `/projetos/abc` de uma futura `/projetosX`.
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * A aba acesa — **a de ORIGEM quando houve origem, a canônica quando não houve**
 * (spec-project-review-ux.md decisão 16, emenda ao ADR-012 §2).
 *
 * A aba responde "em que trabalho eu estou", não "onde este dado mora no
 * estrutura". Quem sai da fila `/analise` e abre um projeto continua
 * analisando: ver a sidebar pular para "Projetos" e o único "voltar" da tela
 * apontar para uma lista por onde a pessoa não passou é a moldura descrevendo a
 * hierarquia canônica no meio de um percurso.
 *
 * **O permalink NÃO muda** (decisão 1 da 1ª rodada): a origem viaja em
 * `location.state.from`, e não na URL — query param sujaria o endereço
 * compartilhado com o percurso de quem compartilhou.
 *
 * ⚠️ **Origem é enfeite de navegação, e isso é regra, não observação.** Ela
 * morre no F5, por construção. **Nada que dependa de autorização, de capacidade
 * ou de estado pode lê-la** — na v1.0 desta spec ela chegou a governar a
 * aparição do painel de decisão, e o resultado era o F5 no meio de uma análise
 * fazer os botões sumirem. Trilha e aba acesa, só.
 *
 * Origem desconhecida (rota que não é de aba nenhuma) cai no canônico, em vez
 * de apagar a marca da sidebar.
 */
export function activeNavItem(
  items: readonly NavItem[],
  pathname: string,
  from?: string,
): NavItem | null {
  const canonical = resolveOwner(items, pathname);
  if (!from) return canonical;

  const origin = resolveOwner(items, from);
  return origin ?? canonical;
}

/**
 * A aba de uma rota **dentro da lista que aquela pessoa enxerga**: o dono
 * canônico, ou quem a adota quando o dono não está visível (decisão 97).
 *
 * A ordem é a que resolve o caso do overseer sem nenhum `if` de papel: ele TEM
 * "Projetos", então a adoção do Calendário nunca é consultada.
 */
function resolveOwner(items: readonly NavItem[], pathname: string): NavItem | null {
  /**
   * **Casamento EXATO antes do casamento por prefixo**, e a passada extra tem um
   * caso concreto: quem é overseer E líder de um ministério tem os dois itens na
   * lista, e "Coordenações" (`owns: ['/ministerios']`, e mais acima em
   * `NAV_ITEMS`) venceria o item do próprio ministério num `find` só. A pessoa
   * clicaria em "Missões" e veria "Coordenações" acender.
   *
   * O mesmo vale, agora, para o item de VÍNCULO de coordenação: ele aponta
   * para `/coordenacoes/<id>`, que o prefixo da aba alcança. Exato antes de
   * prefixo continua sendo a regra que resolve os dois casos sem consultar
   * papel nenhum.
   *
   * Item de vínculo aponta para uma rota inteira (`/ministerios/<id>`) e item
   * estático para um prefixo — então "o mais específico ganha" é a regra certa,
   * e ela vale sem nenhuma consulta a papel.
   */
  const exact = items.find((item) => item.to === pathname);
  if (exact) return exact;

  const owner = items.find((item) => isNavItemActive(item, pathname));
  if (owner) return owner;

  return (
    items.find((item) =>
      (item.fallbackFor ?? []).some(
        // O `/` no fim é o que separa `/projetos/abc` de uma futura `/projetosX`.
        (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
      ),
    ) ?? null
  );
}

/**
 * O primeiro item da trilha: a aba dona da rota, com o mesmo critério de
 * origem da sidebar.
 *
 * Breadcrumb e sidebar leem a MESMA função de propósito — uma trilha dizendo
 * "Análise" com a sidebar acesa em "Projetos" é pior do que qualquer um dos
 * dois sozinho.
 */
export function originCrumb(
  items: readonly NavItem[],
  pathname: string,
  from?: string,
): { label: string; to: string } | null {
  const item = activeNavItem(items, pathname, from);
  return item ? { label: item.label, to: item.to } : null;
}

/**
 * Lê a origem de `location.state` sem confiar nela: `state` é `unknown` no
 * React Router e chega de fora (link colado, histórico antigo, extensão).
 */
export function readNavOrigin(state: unknown): string | undefined {
  if (typeof state !== 'object' || state === null) return undefined;
  const from = (state as { from?: unknown }).from;
  return typeof from === 'string' && from.startsWith('/') ? from : undefined;
}
